import { useEffect, useRef, useState } from "react";

/**
 * Custom glow cursor — a large soft gold spotlight ring that follows the mouse,
 * plus a small solid dot. Grows on hover of interactive elements.
 * Disabled on touch devices.
 */
export const GlowCursor = () => {
  const ringRef = useRef(null);
  const dotRef = useRef(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (isTouch || reduce) return;
    setEnabled(true);

    let rx = window.innerWidth / 2, ry = window.innerHeight / 2;
    let dx = rx, dy = ry;
    let raf;

    const onMove = (e) => {
      dx = e.clientX;
      dy = e.clientY;
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
      }
      const t = e.target;
      const interactive = t.closest("a, button, [role='button'], input, textarea, select, .cursor-grow");
      if (ringRef.current) {
        ringRef.current.style.opacity = interactive ? "1" : "0.6";
        ringRef.current.dataset.hover = interactive ? "true" : "false";
      }
    };

    const loop = () => {
      rx += (dx - rx) * 0.15;
      ry += (dy - ry) * 0.15;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${rx}px, ${ry}px, 0)`;
      }
      raf = requestAnimationFrame(loop);
    };

    window.addEventListener("mousemove", onMove);
    loop();
    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  if (!enabled) return null;

  return (
    <>
      <div
        ref={ringRef}
        aria-hidden="true"
        className="pointer-events-none fixed left-0 top-0 z-[70] -ml-5 -mt-5 h-10 w-10 rounded-full border border-gold/60 transition-[width,height,opacity,border-color] duration-300 data-[hover=true]:h-16 data-[hover=true]:w-16 data-[hover=true]:-ml-8 data-[hover=true]:-mt-8 data-[hover=true]:bg-gold/10"
      />
      <div
        ref={dotRef}
        aria-hidden="true"
        className="pointer-events-none fixed left-0 top-0 z-[71] -ml-[3px] -mt-[3px] h-1.5 w-1.5 rounded-full bg-gold"
      />
    </>
  );
};
